import { useState } from 'react';
import { DialogPanel, DialogTitle, Dialog as HeadlessDialog } from '@headlessui/react';
import { Bell, House, Menu as MenuIcon, ScanBarcode, Search, Tag, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink } from 'react-router';
import { useAppContext } from '@/app/app-context';
import { Avatar } from '@/components/ui/Avatar';
import { UnreadBadge, useUnreadAlerts } from '@/features/alerts/Bell';
import { useAuth, useCurrentUser } from '@/features/auth/auth-context';
import { cn } from '@/lib/cn';
import { BrandMark } from './BrandMark';
import { GlobalSearch } from './GlobalSearch';

/** Full-screen search for phones; also opened by the Scan tab. */
function SearchSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <HeadlessDialog open={open} onClose={onClose} className="relative z-50 lg:hidden">
      <DialogPanel className="fixed inset-0 flex flex-col bg-ground px-4 pt-[max(1rem,env(safe-area-inset-top))]">
        <div className="mb-3 flex items-center justify-between">
          <DialogTitle className="text-section font-semibold">{t('search.label')}</DialogTitle>
          <button
            type="button"
            onClick={onClose}
            className="rounded-control p-2 hover:bg-surface"
            aria-label={t('actions.close')}
          >
            <X className="size-6" />
          </button>
        </div>
        <GlobalSearch variant="sheet" onNavigate={onClose} />
        <p className="mt-4 text-sm text-muted">{t('search.scanHint')}</p>
      </DialogPanel>
    </HeadlessDialog>
  );
}

/** Phone and portrait-tablet header: brand, search, account. */
export function MobileHeader() {
  const { t } = useTranslation();
  const context = useAppContext();
  const user = useCurrentUser();
  const [searching, setSearching] = useState(false);

  return (
    <>
      <div className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-line bg-surface/95 px-4 pt-[env(safe-area-inset-top)] backdrop-blur lg:hidden print:hidden">
        <Link to="/" className="flex min-w-0 items-center gap-2.5">
          <BrandMark logoUrl={context?.company.logoUrl} className="size-10 rounded-[10px]" />
          <span className="min-w-0">
            <span className="block leading-tight font-semibold">Light BM</span>
            <span className="block truncate text-caption text-muted">
              {context?.company.displayName}
            </span>
          </span>
        </Link>
        <button
          type="button"
          onClick={() => setSearching(true)}
          className="ml-auto rounded-control p-2.5 text-ink-2 hover:bg-ground"
          aria-label={t('search.label')}
        >
          <Search className="size-6" strokeWidth={1.75} />
        </button>
        <Link to="/menu" aria-label={t('nav.menu')}>
          <Avatar name={user.fullName} seed={user.username} size="sm" />
        </Link>
      </div>
      <SearchSheet open={searching} onClose={() => setSearching(false)} />
    </>
  );
}

function TabLink({
  to,
  icon: Icon,
  label,
  end,
  badge = 0,
}: {
  to: string;
  icon: typeof House;
  label: string;
  end?: boolean;
  badge?: number;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className="group flex flex-1 flex-col items-center gap-1 pt-2 text-sm text-ink-2"
    >
      {({ isActive }) => (
        <>
          <span
            className={cn(
              'relative flex h-9 w-16 items-center justify-center rounded-full',
              isActive && 'bg-primary-soft text-primary',
            )}
          >
            <Icon className="size-6" strokeWidth={1.75} />
            <UnreadBadge
              count={badge}
              className="absolute -top-1.5 right-1 border-2 border-surface"
            />
          </span>
          <span className={cn(isActive && 'font-semibold text-primary')}>{label}</span>
        </>
      )}
    </NavLink>
  );
}

/** Bottom tab bar of the design ("Component mobile tab bar"). */
export function MobileTabBar() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const unread = useUnreadAlerts();
  const [searching, setSearching] = useState(false);

  return (
    <>
      <nav
        aria-label={t('nav.label')}
        className="fixed inset-x-0 bottom-0 z-30 flex border-t border-line bg-surface pb-[max(0.5rem,env(safe-area-inset-bottom))] lg:hidden print:hidden"
      >
        <TabLink to="/" end icon={House} label={t('nav.home')} />
        {can('products:view') && <TabLink to="/products" icon={Tag} label={t('nav.products')} />}
        <div className="flex flex-1 flex-col items-center">
          <button
            type="button"
            onClick={() => setSearching(true)}
            className="-mt-6 flex size-[68px] items-center justify-center rounded-full border-4 border-surface bg-primary text-white shadow-pop"
            aria-label={t('search.scan')}
          >
            <ScanBarcode className="size-7" strokeWidth={1.9} />
          </button>
          <span className="mt-0.5 text-sm font-semibold text-primary">{t('search.scanShort')}</span>
        </div>
        {can('alerts:view') && (
          <TabLink to="/alerts" icon={Bell} label={t('nav.alerts')} badge={unread} />
        )}
        <TabLink to="/menu" icon={MenuIcon} label={t('nav.menu')} />
      </nav>
      <SearchSheet open={searching} onClose={() => setSearching(false)} />
    </>
  );
}
